import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { differenceInCalendarDays, endOfWeek, isBefore, isWithinInterval, parseISO, startOfDay, startOfWeek } from 'date-fns';
import axiosInstance from '../apiServices/axios';
import { IRL_ITEMS, INTELLIGENCE_INSIGHTS_V2_OVERVIEW } from '../apiServices/api_routes';
import { GetCurrentProjectData } from '../redux/projectDataSlice';
import { GetWorkstreams } from '../redux/dataroomSlice';
import { GetTask } from '../redux/taskSlice';
import { GetProjectUsers } from '../redux/teamSlice';
import { GetRaidItems } from '../redux/raidSlice';
import { GetActionLogs } from '../redux/actionLogSlice';
import { GetLessons } from '../redux/lessonSlice';
import { GetNotes } from '../redux/dashboardSlice';
import { normalizeTaskStatus } from '../utils/taskSchedulingUtils';
import { IRL_OVERVIEW_STATES, resolveIrlOverviewCard } from '../utils/standardOverviewIrl';
import { applyMaturityLayer } from '../utils/standardOverviewMaturity';
import { buildOverviewFeatureFlags } from '../utils/standardOverviewFeatures';

const CLOSED_TASK_STATUSES = new Set(['COMPLETED', 'COMPLETE', 'CANCELLED']);
const CLOSED_RAID_STATUSES = new Set(['COMPLETED', 'CANCELLED']);
function pickSourceMaterials(project) {
    const candidates = [
        project?.source_materials,
        project?.context_documents,
        project?.project_context_documents,
        project?.source_material_documents,
    ];
    for (const list of candidates) {
        if (Array.isArray(list) && list.length) {
            return list.map(item => (typeof item === 'string' ? item : item?.name || item?.filename || item?.document_name)).filter(Boolean);
        }
    }
    return [];
}

function countJournalEntries(notes) {
    if (!Array.isArray(notes)) return 0;
    return notes.reduce((sum, section) => sum + (Array.isArray(section?.notes) ? section.notes.length : 0), 0);
}

function buildTaskStats(taskData) {
    const today = startOfDay(new Date());
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });

    let open = 0;
    let overdue = 0;
    let dueThisWeek = 0;

    (taskData || []).forEach(task => {
        const status = normalizeTaskStatus(task?.status);
        if (CLOSED_TASK_STATUSES.has(status)) return;
        open += 1;
        if (!task?.deadline) return;
        let deadline;
        try {
            deadline = startOfDay(new Date(task.deadline));
        } catch {
            return;
        }
        if (isBefore(deadline, today)) overdue += 1;
        if (isWithinInterval(deadline, { start: weekStart, end: weekEnd })) dueThisWeek += 1;
    });

    const lines = [`${open} open · ${overdue} overdue`];
    if (dueThisWeek > 0) lines.push(`${dueThisWeek} due this week`);
    return { open, overdue, dueThisWeek, lines };
}

function buildRaidStats(raidItems) {
    const active = (raidItems || []).filter(item => !CLOSED_RAID_STATUSES.has(normalizeTaskStatus(item?.status)));
    const highPriority = active.filter(item => String(item?.impact || item?.level || '').toLowerCase() === 'high').length;
    return {
        lines: [`${active.length} active · ${highPriority} high priority`],
    };
}

function buildActionLogStats(actionLogs) {
    const today = startOfDay(new Date());
    let open = 0;
    let overdue = 0;
    (actionLogs || []).forEach(entry => {
        const status = normalizeTaskStatus(entry?.status);
        if (status === 'COMPLETED' || status === 'CANCELLED') return;
        open += 1;
        if (entry?.due_date) {
            try {
                if (isBefore(startOfDay(new Date(entry.due_date)), today)) overdue += 1;
            } catch {
                /* ignore */
            }
        }
    });
    return { lines: [`${open} open · ${overdue} overdue`] };
}

function formatDeadlineMeta(deadlineStr) {
    if (!deadlineStr) return null;
    try {
        const deadline = new Date(deadlineStr);
        const days = differenceInCalendarDays(deadline, new Date());
        const dateLabel = deadline.toISOString().slice(0, 10);
        if (Number.isNaN(days)) return `Deadline: ${dateLabel}`;
        if (days === 0) return `Deadline: ${dateLabel} · Due today`;
        if (days > 0) return `Deadline: ${dateLabel} · ${days} day${days === 1 ? '' : 's'} remaining`;
        const abs = Math.abs(days);
        return `Deadline: ${dateLabel} · ${abs} day${abs === 1 ? '' : 's'} overdue`;
    } catch {
        return null;
    }
}

function buildTeamLabel(projectUsersAll, currentUserId) {
    const members = (projectUsersAll || []).map(row => row?.value).filter(Boolean);
    if (members.length <= 1) return 'Just you';
    if (members.length === 2 && currentUserId && members.some(m => m.user_id === currentUserId)) {
        return 'You + 1 other';
    }
    return `${members.length} team members`;
}

async function fetchFolderCount(workstreams) {
    const projectId = localStorage.getItem('project_id');
    if (!projectId || !workstreams?.length) return 0;
    const results = await Promise.all(
        workstreams.map(async ws => {
            try {
                const { data } = await axiosInstance.get(`${IRL_ITEMS}${projectId}/${ws.id}/`);
                return Array.isArray(data) ? data.length : 0;
            } catch {
                return 0;
            }
        }),
    );
    return results.reduce((sum, n) => sum + n, 0);
}

export function useStandardOverviewData() {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.projectData);
    const { workstreams } = useSelector(state => state.dataroom);
    const { taskData } = useSelector(state => state.task);
    const { projectUsersAll } = useSelector(state => state.team);
    const { raidItems } = useSelector(state => state.raid || {});
    const { actionLogs } = useSelector(state => state.actionLog || {});
    const { lessons } = useSelector(state => state.lesson || {});
    const notes = useSelector(state => state.dashboard?.notes || []);
    const user = useSelector(state => state.user?.user);

    const [folderCount, setFolderCount] = useState(0);
    const [foldersLoading, setFoldersLoading] = useState(false);
    const [insightsPayload, setInsightsPayload] = useState(null);

    useEffect(() => {
        dispatch(GetCurrentProjectData());
        dispatch(GetWorkstreams());
        dispatch(GetTask());
        dispatch(GetProjectUsers());
        dispatch(GetRaidItems());
        dispatch(GetActionLogs());
        dispatch(GetLessons());
        dispatch(GetNotes());
    }, [dispatch]);

    useEffect(() => {
        let cancelled = false;
        if (!workstreams?.length) {
            setFolderCount(0);
            return undefined;
        }
        setFoldersLoading(true);
        fetchFolderCount(workstreams)
            .then(count => {
                if (!cancelled) setFolderCount(count);
            })
            .finally(() => {
                if (!cancelled) setFoldersLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [workstreams]);

    const projectId = selectedProject?.id || localStorage.getItem('project_id');
    const features = useMemo(
        () => buildOverviewFeatureFlags(selectedProject?.enabled_features),
        [selectedProject?.enabled_features],
    );
    const documentCount = selectedProject?.vdr?.documents_count || 0;
    const workstreamCount = workstreams?.length || 0;
    const hasWorkstreams = workstreamCount > 0;
    const vdrConfigured = hasWorkstreams && folderCount > 0;
    const vdrPopulated = documentCount > 0;
    const showInsightsSections = vdrConfigured && features.showExtendedInsights;
    const showDeliveryKnowledgeSections =
        vdrConfigured && (features.deliveryGovernance || features.projectKnowledge);
    const showExtendedSections = showInsightsSections;

    useEffect(() => {
        if (!projectId || !showInsightsSections || !features.lensIntelligence) {
            setInsightsPayload(null);
            return undefined;
        }
        let cancelled = false;
        axiosInstance
            .get(INTELLIGENCE_INSIGHTS_V2_OVERVIEW(projectId))
            .then(res => {
                if (!cancelled) setInsightsPayload(res?.data || null);
            })
            .catch(() => {
                if (!cancelled) setInsightsPayload(null);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId, showInsightsSections, features.lensIntelligence]);

    const model = useMemo(() => {
        const progress = selectedProject?.projectprogress || {};
        const irlCompleted = selectedProject?.irl_completed || 0;
        const irlOutstanding = selectedProject?.irl_outstanding || 0;
        const irlTotal = irlCompleted + irlOutstanding;

        const observationCount =
            (progress.strengths || 0) +
            (progress.weaknesses || 0) +
            (progress.opportunities || 0) +
            (progress.threats || 0);

        const latestDocs = selectedProject?.vdr?.documents_latest || [];
        const vdrLatestUploadAt = latestDocs[0]?.created || latestDocs[0]?.updated || null;
        const vdrPreviewFiles = latestDocs.slice(0, 3).map(doc => doc?.name).filter(Boolean);

        const metaItems = [
            selectedProject?.investment_type,
            formatDeadlineMeta(selectedProject?.project_deadline),
            hasWorkstreams ? `${workstreamCount} workstream${workstreamCount === 1 ? '' : 's'}` : null,
            buildTeamLabel(projectUsersAll, user?.id),
        ].filter(Boolean);

        const modulesNeedingReview = (insightsPayload?.data?.modules || []).filter(
            m => m?.status === 'complete_with_warnings' || m?.new_evidence_available,
        ).length;
        const newInsights = (insightsPayload?.data?.modules || []).filter(m => m?.new_evidence_available).length;

        const intelligenceReady = vdrPopulated && Boolean(insightsPayload?.analysis?.version);
        const intelligence = intelligenceReady
            ? {
                  ready: true,
                  needsReview: modulesNeedingReview > 0,
                  headline: newInsights > 0 ? `${newInsights} new insights` : 'Analysis available',
                  subline: modulesNeedingReview > 0 ? `${modulesNeedingReview} require review` : null,
                  meta: insightsPayload?.analysis?.generated_at
                      ? `Last analysed ${new Date(insightsPayload.analysis.generated_at).toLocaleString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                            day: 'numeric',
                            month: 'short',
                        })}`
                      : null,
              }
            : { ready: false };

        const irlCard = resolveIrlOverviewCard({
            project: selectedProject,
            hasWorkstreams,
            vdrConfigured,
            vdrPopulated,
            documentCount,
            irlCompleted,
            irlOutstanding,
        });

        let highlight = null;
        if (irlCard.highlight) highlight = 'irl';
        else if (!hasWorkstreams) highlight = 'workstreams';
        else if (hasWorkstreams && !vdrConfigured) highlight = 'vdr';

        const showRecentActivity =
            irlCard.showRecentActivity ||
            irlCard.state === IRL_OVERVIEW_STATES.FINDINGS_READY;

        return applyMaturityLayer(
            {
                loading: foldersLoading && hasWorkstreams,
                phase: !hasWorkstreams ? 'shell' : !vdrConfigured ? 'workstreams_only' : vdrPopulated ? 'vdr_populated' : 'vdr_ready',
                highlight,
                projectName: selectedProject?.project_name || '—',
                subtitle: [selectedProject?.project_type, selectedProject?.investor_name || selectedProject?.company_name].filter(Boolean).join(' · ') || '—',
                metaItems,
                hasWorkstreams,
                workstreamCount,
                folderCount,
                documentCount,
                vdrConfigured,
                vdrPopulated,
                showExtendedSections,
                showInsightsSections,
                showDeliveryKnowledgeSections,
                features,
                vdrLatestUploadAt,
                vdrPreviewFiles,
                sourceMaterials: pickSourceMaterials(selectedProject),
                irl: {
                    hasIrl: irlTotal > 0,
                    completed: irlCompleted,
                    outstanding: irlOutstanding,
                },
                irlCard,
                showRecentActivity,
                observationCount,
                intelligence,
                swot: {
                    strengths: progress.strengths || 0,
                    weaknesses: progress.weaknesses || 0,
                    opportunities: progress.opportunities || 0,
                    threats: progress.threats || 0,
                    updatedLabel: progress.last_updated
                        ? `Updated ${formatDistanceLabel(progress.last_updated)}`
                        : null,
                },
                indexScores: {
                    maturity: progress.maturity_score ?? null,
                    security: progress.security_score ?? null,
                    governance: progress.governance_score ?? null,
                    teamAnalyser: {
                        exemplary: progress.exemplary_score || 0,
                        adequate: progress.adequate_score || 0,
                        development: progress.development_score || 0,
                    },
                },
                delivery: {
                    tasks: buildTaskStats(taskData),
                    raid: buildRaidStats(raidItems),
                    actionLog: buildActionLogStats(actionLogs),
                },
                knowledge: {
                    lessonsCount: Array.isArray(lessons) ? lessons.length : 0,
                    journalEntryCount: countJournalEntries(notes),
                },
            },
            { project: selectedProject },
        );
    }, [
        selectedProject,
        workstreams,
        workstreamCount,
        hasWorkstreams,
        folderCount,
        foldersLoading,
        documentCount,
        vdrConfigured,
        vdrPopulated,
        showExtendedSections,
        showInsightsSections,
        showDeliveryKnowledgeSections,
        features,
        taskData,
        raidItems,
        actionLogs,
        lessons,
        notes,
        projectUsersAll,
        user?.id,
        insightsPayload,
    ]);

    return model;
}

function formatDistanceLabel(iso) {
    try {
        const d = typeof iso === 'string' ? parseISO(iso) : new Date(iso);
        const days = differenceInCalendarDays(new Date(), d);
        if (days === 0) return 'today';
        if (days === 1) return '1 day ago';
        if (days > 1) return `${days} days ago`;
        return 'recently';
    } catch {
        return '';
    }
}
